import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, MapPin } from "lucide-react";

import { SeoHead } from "@/components/seo/SeoHead";
import { Button } from "@/components/ui/button";
import { SITE_URL, SITE_NAME } from "@/lib/seo";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const MANIFESTO_KEYS = ["one", "two", "three", "four"] as const;
const TIMELINE_KEYS = ["idea", "mvp", "firstClients", "expansion", "team"] as const;
const PRESS_KEYS = ["pending"] as const;

export default function AboutPage() {
  const { t } = useTranslation();

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: t("marketing.about.heading"),
    description: t("marketing.about.seoDescription"),
    url: `${SITE_URL}/a-propos`,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={t("marketing.about.seoTitle")}
        description={t("marketing.about.seoDescription")}
        path="/a-propos"
        jsonLd={[aboutJsonLd]}
      />
      <motion.div
        className="mx-auto max-w-3xl px-5 py-12 md:py-20"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <header className="mb-14 text-center">
          <p className="dateline mb-3">{t("marketing.about.eyebrow")}</p>
          <h1 className="font-serif text-4xl font-bold leading-tight text-foreground md:text-5xl">
            {t("marketing.about.heading")}
          </h1>
          <div className="mx-auto my-5 h-px w-16 bg-foreground/40" />
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground">
            {t("marketing.about.lead")}
          </p>
        </header>

        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {t("marketing.about.manifesto.title")}
          </h2>
          <div className="mt-4 space-y-4 text-base leading-7 text-foreground/90">
            {MANIFESTO_KEYS.map((k) => (
              <p key={k}>{t(`marketing.about.manifesto.paragraphs.${k}`)}</p>
            ))}
          </div>
        </section>

        <section className="mb-16 rounded-lg border border-border bg-card p-6 md:p-8">
          <div className="flex flex-col items-start gap-6 sm:flex-row">
            <div
              className="h-20 w-20 flex-shrink-0 rounded-full bg-gradient-to-br from-primary/20 to-primary/5"
              aria-hidden
            />
            <div className="flex-1">
              <p className="dateline mb-2">{t("marketing.about.founder.dateline")}</p>
              <h2 className="font-serif text-xl font-semibold text-foreground">
                {t("marketing.about.founder.name")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t("marketing.about.founder.role")}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground/90">
                {t("marketing.about.founder.bio")}
              </p>
              <blockquote className="mt-4 border-l-2 border-primary pl-4 text-sm italic leading-relaxed text-muted-foreground">
                {t("marketing.about.founder.note")}
              </blockquote>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {t("marketing.about.timeline.title")}
          </h2>
          <ol className="mt-6 space-y-6 border-l border-border pl-6">
            {TIMELINE_KEYS.map((k) => (
              <li key={k} className="relative">
                <span
                  className="absolute -left-[27px] top-1 inline-block h-2 w-2 rounded-full bg-primary"
                  aria-hidden
                />
                <p className="dateline text-xs">
                  {t(`marketing.about.timeline.items.${k}.date`)}
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {t(`marketing.about.timeline.items.${k}.title`)}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {t(`marketing.about.timeline.items.${k}.body`)}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {t("marketing.about.press.title")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("marketing.about.press.lead")}
          </p>
          <ul className="mt-5 grid gap-3 sm:grid-cols-3">
            {PRESS_KEYS.map((k) => (
              <li
                key={k}
                className="flex items-center justify-center rounded-lg border border-dashed border-border p-6 text-xs text-muted-foreground"
              >
                {t(`marketing.about.press.items.${k}`)}
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-12 rounded-lg border border-border bg-card p-6 md:p-8">
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {t("marketing.about.join.title")}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {t("marketing.about.join.body")}
          </p>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <Button asChild>
              <a href="mailto:rejoindre@delapresse.fr?subject=Candidature%20spontan%C3%A9e">
                {t("marketing.about.join.applyButton")}
                <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
              </a>
            </Button>
            <Button asChild variant="outline">
              <Link to="/contact?subject=other">{t("marketing.about.join.contactButton")}</Link>
            </Button>
          </div>
          <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" aria-hidden />
            {t("marketing.about.join.location")}
          </p>
        </section>
      </motion.div>
    </div>
  );
}
